import type { TagFragment } from "@src/__generated__/graphql";
import type { AppStore } from "@src/stores/AppStore";
import type { BaseStore } from "@src/stores/BaseStore";
import type { ModalStore } from "@src/stores/ModalStore";
import { TagEditModal } from "@src/stores/tag-store/modal/index";
import { required } from "@src/utils/forms/validators";
import { DisclosureState } from "@src/utils/mobx/states/DisclosureState";
import { FieldState } from "formstate";
import { action, computed, makeObservable, observable } from "mobx";

export class TagModalStore implements BaseStore, ModalStore {
  appStore: AppStore;
  readonly modalId = "tagEditModal";
  newTagField = new FieldState<string>("").validators(required);
  @observable editingTagIds = new Set<TagFragment["id"]>();

  @observable modalState = new DisclosureState<Record<string, never>>({
    onClose: () => {
      this.appStore.UIStore.dialogs.closeModal(this.modalId);
    },
    onOpen: () => {
      this.appStore.UIStore.dialogs.openModal({
        id: this.modalId,
        content: <TagEditModal />,
      });
      this.appStore.tagStore.fetchTags();
    },
  });

  constructor(appStore: AppStore) {
    makeObservable(this);
    this.appStore = appStore;
  }

  @computed get tagRows() {
    return this.appStore.tagStore.tags.map((tag) => ({
      id: tag.id,
      field: new FieldState(tag.title).validators(required),
    }));
  }

  async handleDeleteTag(id: TagFragment["id"]) {
    await this.appStore.tagStore.deleteTag(id);
  }

  @action.bound async handleCreateTag() {
    const validation = await this.newTagField.validate();

    if (validation.hasError) {
      return;
    }

    await this.appStore.tagStore.createTag(validation.value);
    this.newTagField.reset();
  }

  @action.bound handleEditTag(id: TagFragment["id"]) {
    this.editingTagIds.add(id);
  }

  @action.bound handleCancelEditTag(id: TagFragment["id"]) {
    this.tagRows.find((row) => row.id === id)?.field.reset();
    this.editingTagIds.delete(id);
  }

  @action.bound async handleConfirmEditTag(
    id: TagFragment["id"],
    title: TagFragment["title"],
  ) {
    await this.appStore.tagStore.updateTag(id, title);
    this.editingTagIds.delete(id);
  }
}
